/**
 * Claims of a contract or a vehicle (« Sinistres » tab — fiches 22719 /
 * 22776): a `DataList` with the stable columns `reference` (NCBT_SIN),
 * `circonstances` (LIB_CIRC), `dateSurvenance` (DATS_SIN, ms → JJ/MM/AAAA)
 * and `coutAssureur` (€, 2 decimals), rows in API order; a row with a `to`
 * path opens it on click (the claim detail).
 *
 * Rows are mapped by the domain service (view model `ClaimTableRow`); the
 * columns live in `CLAIMS_TABLE_COLUMNS` (`./claimsTableConfig.ts`).
 * Overrides / publish / states are handled by `DataTableCard`.
 *
 * NOT registered by itself — registered per usage
 * (`extranet.contractClaimsCard`, `extranet.vehicleClaimsCard`).
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, type LucideIcon } from 'lucide-react';

import type { LayoutOverrides } from '@components/shared/configurable';

import type { ExtranetRowContext } from './cellRenderers';
import { buildClaimsTableConfig, type ClaimTableRow } from './claimsTableConfig';
import { DataTableCard } from './DataTableCard';

export type { ClaimTableRow } from './claimsTableConfig';

export interface ClaimsTableCardProps {
  /** Registered id of this usage (bound by `bindCardProps`). */
  componentId: string;
  /** Base title (overridable); omitted ⇒ no title row. */
  title?: string;
  icon?: LucideIcon;
  rows: readonly ClaimTableRow[];
  loading?: boolean;
  error?: string | null;
  /** Injected by `withComponentRules` — never passed by hand. */
  _layoutOverrides?: LayoutOverrides;
}

export function ClaimsTableCard({
  componentId,
  title,
  icon = AlertTriangle,
  rows,
  loading = false,
  error = null,
  _layoutOverrides,
}: Readonly<ClaimsTableCardProps>) {
  const navigate = useNavigate();

  // Per-row context: the click target of each row, rebuilt with the rows so
  // the singleton row-click handler always reads fresh closures.
  const listRows = useMemo(
    () =>
      rows.map((row) => {
        const to = row.to;
        const ctx: ExtranetRowContext = {
          onOpen: to
            ? () => {
                void navigate(to);
              }
            : undefined,
        };
        return { ...row, __ctx: ctx };
      }),
    [rows, navigate],
  );

  const config = useMemo(
    () => buildClaimsTableConfig(componentId, listRows),
    [componentId, listRows],
  );

  return (
    <DataTableCard
      componentId={componentId}
      title={title}
      icon={icon}
      config={config}
      rowCount={rows.length}
      loading={loading}
      error={error}
      _layoutOverrides={_layoutOverrides}
    />
  );
}
